import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Layout from '../components/Layout';
import { resourcesSchemas } from '../lib/schemas';

/* ── Hero ── */
const HeroSection = styled.section`
  background-color: #7AA6A2;
  padding: 6rem 2rem 7rem;
  text-align: center;
  position: relative;
  z-index: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 65%;
    height: 100%;
    background-image: url('/img/background.png');
    background-repeat: no-repeat;
    background-size: cover;
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 50%);
    mask-image: linear-gradient(to right, transparent 0%, black 50%);
    pointer-events: none;
    z-index: -1;
  }
`;

const HeroWave = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  line-height: 0;
  pointer-events: none;
  svg { width: 100%; height: 64px; display: block; }
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

/* ── Lead (below hero) ── */
const LeadSection = styled.section`
  background-color: #fff;
  padding: 4rem 2rem 4.5rem;
  text-align: center;
`;

const LeadText = styled.p`
  color: #555;
  font-size: 1.2rem;
  line-height: 1.8;
  max-width: 580px;
  margin: 0 auto;
`;

/* ── Section shell ── */
const ContentSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1100px;
  margin: 0 auto;
`;

const SectionLabel = styled.p`
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #7AA6A2;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const SectionSubtitle = styled.p`
  color: #666;
  font-size: 1.05rem;
  line-height: 1.7;
  margin-bottom: 3rem;
`;

/* ── Book card ── */
const BookList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.75rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const BookCard = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding: 1.25rem 1.5rem;
  gap: 1.25rem;
`;

const BookCover = styled.img`
  width: 80px;
  min-width: 80px;
  aspect-ratio: 2 / 3;
  object-fit: cover;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  align-self: flex-start;
`;

const BookBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
`;

const BookTitle = styled.h3`
  color: #333;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  line-height: 1.4;
`;

const BookAuthor = styled.p`
  color: #7AA6A2;
  font-size: 0.88rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const BookDescription = styled.p`
  color: #555;
  font-size: 0.95rem;
  line-height: 1.75;
  margin: 0 0 1rem;
  flex: 1;
  white-space: pre-line;
`;

const STATUS_STYLES = {
  available:    { bg: '#e8f5f4', color: '#3d7a75' },
  in_progress:  { bg: '#fff8e6', color: '#8a6500' },
  coming_soon:  { bg: '#f2f2f2', color: '#777' },
};

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.3rem 0.85rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  align-self: flex-start;
  background-color: ${({ status }) => STATUS_STYLES[status].bg};
  color: ${({ status }) => STATUS_STYLES[status].color};
`;

/* ── Notify form ── */
const NotifySection = styled.section`
  background-color: #7AA6A2;
  padding: 5rem 2rem;
  text-align: center;
`;

const NotifyTitle = styled.h3`
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const NotifySubtext = styled.p`
  color: rgba(255,255,255,0.9);
  margin-bottom: 1.5rem;
  font-size: 1rem;
`;

const NotifyForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;
  max-width: 560px;
  margin: 0 auto;
`;

const NotifyRow = styled.div`
  display: flex;
  gap: 0.65rem;
  width: 100%;
  flex-wrap: wrap;
`;

const EmailInput = styled.input`
  padding: 0.65rem 1rem;
  border: 1px solid #ccc;
  border-radius: 100px;
  font-size: 1rem;
  flex: 1;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #7AA6A2;
    box-shadow: 0 0 0 2px rgba(122,166,162,0.2);
  }
`;

const NotifyButton = styled.button`
  background-color: #fff;
  color: #7AA6A2;
  border: none;
  padding: 0.65rem 1.4rem;
  border-radius: 100px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: #5a8a87;
    color: #fff;
  }
`;

const SuccessMessage = styled.p`
  color: #fff;
  font-weight: 600;
  margin-top: 0.5rem;
`;

/* ── Data ── */
const BOOKS = [
  {
    title: 'AI and the Christian Life',
    author: 'Andrew Schwabe & Rebecca Joy',
    status: 'coming_soon',
    statusLabel: 'Coming Soon',
    cover: '/img/cover_ai_christian_life.jpg',
    description: 'How should Christians think about artificial intelligence? This book will provide theological foundations, ethical frameworks, and practical wisdom for engaging AI in ways that honor God and serve humanity — written by two people who have spent years building AI systems professionally.',
  },
  {
    title: 'Building on the Rock: A Biblical Pre-Marriage Counselling Curriculum',
    author: 'Rebecca Joy',
    status: 'available',
    statusLabel: 'Complete',
    cover: '/img/Building_on_the_Rock_Premarital_Couseling_Curriculum.png',
    description: 'Building on the Rock is an eleven-session, biblically grounded premarital counseling program spanning six months of bi-weekly sessions. It guides couples through the most important dimensions of married life — from the theology of covenant and love to communication, conflict, finances, intimacy, and roles. The complete curriculum includes a Student Guide, Student Workbook, Counsellor\'s Guide, and an Addendum for couples navigating past sexual history.',
  },
  {
    title: 'The Living Foundation: A Book-by-Book Devotional Journey',
    author: 'Rebecca Joy',
    status: 'available',
    statusLabel: 'Complete',
    cover: '/img/the_living_foundation.jpg',
    description: 'A devotional that walks readers through the Bible book by book, uncovering the living, breathing character of God\'s Word. Written to help individuals and small groups root their faith in the whole counsel of Scripture.',
  },
  {
    title: 'Our Journey to Vietnam',
    author: 'Andrew Schwabe & Rebecca Joy',
    status: 'coming_soon',
    statusLabel: 'Coming Soon',
    cover: '/img/cover_our_journey_vietnam.jpg',
    description: 'The story of how two American entrepreneurs ended up building companies in one of the world\'s fastest-growing tech ecosystems — and what they learned about faith, calling, and business as a mission along the way.',
  },
  {
    title: 'Uninvited: For the Woman Who Wonders If She Belongs',
    author: 'Rebecca Joy',
    status: 'in_progress',
    statusLabel: 'In Progress',
    cover: '/img/uninvited.jpg',
    description: 'This is the story of building a career in technology the hard way; through startups, hostile takeovers, a dot-com crash, a move to Southeast Asia, and thirty years of proving myself in rooms that weren\'t built for me. It\'s about what happens when you keep showing up anyway.\n\nThis isn\'t a book about defiance or triumph over adversity. It\'s an honest account of what it actually costs to be first - and what you build because of it.',
  },
  {
    title: 'Upon This Rock: A 36-Week Discipleship Curriculum',
    author: 'Rebecca Joy',
    status: 'available',
    statusLabel: 'Complete',
    cover: '/img/Upon_This_Rock_Discipleship_Guide.jpg',
    description: 'Upon This Rock is a complete small group discipleship curriculum taking new and growing believers through the foundational teachings of the Christian faith. The 36-week journey is organized into six thematic units — covering who God is, how we live, the inner life, calling and spiritual gifts, life together in the Church, and global mission. It comes in two volumes: a Participant Workbook and a Leader Guide with facilitation guides, illustrations, and preparation notes.',
  },
];

const HUBSPOT_PORTAL_ID = '244482894';
const HUBSPOT_FORM_ID = 'f0e81746-60a3-4fd5-8653-a1c9a563bf7e';

const NotifyMeForm = () => {
  const [fields, setFields] = useState({ firstName: '', lastName: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => setFields({ ...fields, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fields: [
              { name: 'firstname', value: fields.firstName },
              { name: 'lastname',  value: fields.lastName  },
              { name: 'email',     value: fields.email     },
            ],
            context: {
              pageUri: window.location.href,
              pageName: 'Resources',
            },
          }),
        }
      );
      if (!res.ok) {
        const body = await res.text();
        console.error('HubSpot submission failed:', res.status, body);
        setError(true);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      console.error('HubSpot submission error:', err);
      setError(true);
    }
  };

  const upcomingBooks = BOOKS
    .filter(b => b.status === 'in_progress' || b.status === 'coming_soon')
    .sort((a, b) => a.title.localeCompare(b.title));

  const formatList = (books) => {
    if (books.length === 0) return null;
    if (books.length === 1) return <em>{books[0].title}</em>;
    return books.map((b, i) => (
      <span key={b.title}>
        {i > 0 && (i === books.length - 1 ? ', and ' : ', ')}
        <em>{b.title}</em>
      </span>
    ));
  };

  return (
    <NotifySection>
      <NotifyTitle>Get notified when new books launch</NotifyTitle>
      <NotifySubtext>
        We'll email you when {formatList(upcomingBooks)} {upcomingBooks.length === 1 ? 'is' : 'are'} available.
      </NotifySubtext>
      {submitted ? (
        <SuccessMessage>You're on the list — we'll be in touch!</SuccessMessage>
      ) : (
        <NotifyForm onSubmit={handleSubmit}>
          <NotifyRow>
            <EmailInput
              type="text"
              name="firstName"
              placeholder="First name"
              value={fields.firstName}
              onChange={handleChange}
              required
              aria-label="First name"
            />
            <EmailInput
              type="text"
              name="lastName"
              placeholder="Last name"
              value={fields.lastName}
              onChange={handleChange}
              required
              aria-label="Last name"
            />
          </NotifyRow>
          <NotifyRow>
            <EmailInput
              type="email"
              name="email"
              placeholder="your@email.com"
              value={fields.email}
              onChange={handleChange}
              required
              aria-label="Email address"
            />
            <NotifyButton type="submit">Notify Me</NotifyButton>
          </NotifyRow>
          {error && <SuccessMessage style={{ color: '#ffe0e0' }}>Something went wrong — please try again.</SuccessMessage>}
        </NotifyForm>
      )}
    </NotifySection>
  );
};

const Resources = () => {
  const bookListRef = useRef(null);

  useEffect(() => {
    const equalizeHeights = () => {
      if (!bookListRef.current) return;
      const cards = bookListRef.current.querySelectorAll('.book-card');
      cards.forEach(card => (card.style.height = 'auto'));
      const maxHeight = Math.max(...Array.from(cards).map(card => card.offsetHeight));
      cards.forEach(card => (card.style.height = `${maxHeight}px`));
    };
    equalizeHeights();
    window.addEventListener('resize', equalizeHeights);
    return () => window.removeEventListener('resize', equalizeHeights);
  }, []);

  return (
    <Layout
      title="Resources"
      description="Books by Building on Truth on AI, entrepreneurship, discipleship, and business as a mission."
      path="/resources"
      image="/img/cover_ai_christian_life.jpg"
      imageAlt="Books and resources by Building on Truth on AI, faith, and entrepreneurship"
      keywords="Christian AI book, business as mission book, discipleship resources, Andrew Schwabe books, Rebecca Schwabe books"
      jsonLd={resourcesSchemas(BOOKS)}
    >
      <HeroSection>
        <HeroTitle>Resources</HeroTitle>
        <HeroWave>
          <svg viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64 C360,0 1080,0 1440,64 L1440,64 L0,64 Z" fill="#fff" />
          </svg>
        </HeroWave>
      </HeroSection>

      <LeadSection>
        <LeadText>
          Whether you're newly engaged, navigating your career, or trying to make sense of your faith in a changing world — there's something here for you.
        </LeadText>
      </LeadSection>

      <ContentSection>
        <SectionLabel>Books</SectionLabel>
        <SectionTitle>Our Books</SectionTitle>
        <SectionSubtitle>
          From premarital counseling to AI ethics, each title is written to help you think more clearly and live more faithfully.
        </SectionSubtitle>

        <BookList ref={bookListRef}>
          {BOOKS.map((book) => (
            <BookCard key={book.title} className="book-card">
              <BookCover src={book.cover} alt={book.title} />
              <BookBody>
                <BookTitle>{book.title}</BookTitle>
                <BookAuthor>{book.author}</BookAuthor>
                <BookDescription>{book.description}</BookDescription>
                <StatusBadge status={book.status}>{book.statusLabel}</StatusBadge>
              </BookBody>
            </BookCard>
          ))}
        </BookList>

      </ContentSection>

      <NotifyMeForm />
    </Layout>
  );
};

export default Resources;
